import { cartItemReferenceStore$ } from './cart-item-reference.store';
import type { CartItemReference } from '../models';
import type { Maybe } from '../types/front.graphql';

/**
 * CART ITEM REFERENCE STORE ACTIONS
 *
 * Internal actions layer that provides controlled access to the store.
 * This layer sits between the public API and the raw store observable.
 *
 * ARCHITECTURE PRINCIPLES:
 * - Encapsulation: Hides direct store access
 * - Validation: Ensures data integrity before mutations
 * - Logging: Centralizes logging for debugging
 * - Type Safety: Properly typed functions
 */

// ============================================================================
// VALIDATION HELPERS - INTERNAL USE ONLY
// ============================================================================

/**
 * Validates that a CartItemReference has a non-null cartItem
 * @throws Error if cartItem is undefined
 */
const validateCartItemReference = (item: CartItemReference, operation: string): void => {
    if (!item.cartItem) {
        throw new Error(
            `[CartItemReferenceStoreActions] Cannot ${operation}: cartItem is undefined for item ${item.cartItemReferenceId}`
        );
    }
};

// ============================================================================
// STATE GETTERS - INTERNAL USE ONLY
// ============================================================================

export const cartItemReferenceStoreActions = {
    // Store identification
    getStoreId: (): string => cartItemReferenceStore$.id.peek() ?? '',

    // Items access - immutable
    getItems: (): CartItemReference[] => {
        const items: Maybe<CartItemReference>[] = cartItemReferenceStore$.items.peek() ?? [];
        return items.filter((item: Maybe<CartItemReference>): item is CartItemReference => !!item);
    },

    // Complete state snapshot
    getState: () => {
        const items: Maybe<CartItemReference>[] = cartItemReferenceStore$.items.peek() ?? [];
        return {
            id: cartItemReferenceStore$.id.peek() ?? '',
            items: items.filter((item: Maybe<CartItemReference>): item is CartItemReference => !!item),
            updatedAt: cartItemReferenceStore$.updatedAt?.peek(),
        };
    },

    // ============================================================================
    // MUTATIONS - INTERNAL USE ONLY
    // ============================================================================

    // Store identification mutations
    setStoreId: (id: string): void => {
        cartItemReferenceStore$.id.set(id);
    },

    // Items mutations
    setItems: (items: CartItemReference[]): void => {
        // Filter out items with undefined cartItem
        const validItems = items.filter(item => !!item.cartItem);

        if (validItems.length < items.length) {
            const invalidCount = items.length - validItems.length;
            const invalidItems = items.filter(item => !item.cartItem);
            console.warn(
                `[CartItemReferenceStoreActions] Filtered out ${invalidCount} item(s) with undefined cartItem`,
                {
                    invalidRefs: invalidItems.map(item => ({
                        id: item.cartItemReferenceId,
                        sku: item.sku,
                        productId: item.productId,
                        qty: item.qty,
                    })),
                }
            );
        }

        // Validate that no CartItemReference has qty <= 0
        for (const item of validItems) {
            if (item.qty <= 0) {
                console.error('[CartItemReferenceStoreActions] Validation failed - invalid qty:', {
                    cartItemReferenceId: item.cartItemReferenceId,
                    qty: item.qty,
                    item,
                });
                throw new Error(
                    `[CartItemReferenceStoreActions] Cannot set items: CartItemReference ${item.cartItemReferenceId} has invalid qty ${item.qty} (must be > 0)`
                );
            }
        }

        console.log('[CartItemReferenceStoreActions] Setting items:', validItems);
        cartItemReferenceStore$.items.set(validItems);
    },

    addItem: (item: CartItemReference): void => {
        validateCartItemReference(item, 'add item');

        // Validate that CartItemReference has qty > 0
        if (item.qty <= 0) {
            console.error('[CartItemReferenceStoreActions] Validation failed - invalid qty:', {
                cartItemReferenceId: item.cartItemReferenceId,
                qty: item.qty,
                item,
            });
            throw new Error(
                `[CartItemReferenceStoreActions] Cannot add item: CartItemReference ${item.cartItemReferenceId} has invalid qty ${item.qty} (must be > 0)`
            );
        }

        const currentItems = cartItemReferenceStoreActions.getItems();
        console.log('[CartItemReferenceStoreActions] Adding item:', item.cartItemReferenceId);
        cartItemReferenceStore$.items.set([...currentItems, item]);
    },

    updateItem: (item: CartItemReference): void => {
        validateCartItemReference(item, 'update item');

        // Validate that CartItemReference has qty > 0
        if (item.qty <= 0) {
            throw new Error(
                `[CartItemReferenceStoreActions] Cannot update item: CartItemReference ${item.cartItemReferenceId} has invalid qty ${item.qty} (must be > 0)`
            );
        }

        const currentItems = cartItemReferenceStoreActions.getItems();
        cartItemReferenceStore$.items.set(
            currentItems.map(r => (r.cartItemReferenceId === item.cartItemReferenceId ? item : r))
        );
    },

    removeItem: (cartItemReferenceId: string): void => {
        const currentItems = cartItemReferenceStoreActions.getItems();
        console.log('[CartItemReferenceStoreActions] Removing item:', cartItemReferenceId);
        cartItemReferenceStore$.items.set(currentItems.filter(r => r.cartItemReferenceId !== cartItemReferenceId));
    },

    removeItemsByCartId: (cartId: string): void => {
        const currentItems = cartItemReferenceStoreActions.getItems();
        const filteredItems = currentItems.filter(r => r.cartId !== cartId);
        console.log(
            '[CartItemReferenceStoreActions] Removing items with cartId:',
            cartId,
            'Removed:',
            currentItems.length - filteredItems.length
        );
        cartItemReferenceStore$.items.set(filteredItems);
    },

    // quantityStepper access
    getQuantityStepper: (): number => cartItemReferenceStore$.quantityStepper.peek() ?? 1,

    setQuantityStepper: (value: number): void => {
        console.log('[CartItemReferenceStoreActions] Setting quantityStepper:', value);
        cartItemReferenceStore$.quantityStepper.set(value);
    },

    // Bulk operations
    resetStore: (): void => {
        console.log('[CartItemReferenceStoreActions] Resetting store');
        // Set each property individually so Legend-State detects changes
        // and syncs them to IndexedDB. Using .set({...}) on the root
        // may not properly clear all persisted keys.
        cartItemReferenceStore$.id.set('');
        cartItemReferenceStore$.items.set([]);
        cartItemReferenceStore$.updatedAt.set(new Date().toISOString());
        cartItemReferenceStore$.quantityStepper.set(1);
    },

    // Update timestamp
    updateTimestamp: (): void => {
        cartItemReferenceStore$.updatedAt?.set(new Date().toISOString());
    },
} as const;

export type CartItemReferenceStoreActions = typeof cartItemReferenceStoreActions;
